/* ============================================================
 * frontend-v3/src/screens/schedule/ScheduleValidationDrawer.tsx
 *
 * T316 / V3I-FR-005 — Category A5 decomposition: schedule validation drawer.
 *
 * Per PRD §7.1 A5 the drawer binds the per-schedule compliance read:
 *   useQuery(api.compliance.checkSchedule, { scheduleId })
 *
 * Args contract (verified at convex/compliance.ts:371):
 *   scheduleId: Id<"schedules">  (required)
 *
 * Replaces the prior hard-coded `groups` array (which was a fabricated
 * placeholder) with the real check result. The prototype's empty-state
 * lives at the drawer's body while the query is pending or empty.
 *
 * A5 is a query — no mutation-policy entry.
 * ============================================================ */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import {
  Btn, EmptyState, Icon, Pill, SlideOver, Skeleton, TONES,
} from '../../components/primitives';

interface ScheduleValidationDrawerProps {
  open: boolean;
  onClose: () => void;
  scheduleId?: string;
}

type ProfileShape = {
  width?: number;
  height?: number;
  frameRate?: number;
  audioSampleRate?: number;
};

type Outlier = {
  scheduleItemId: string;
  assetId: string;
  title: string;
  profile: ProfileShape;
  majority: ProfileShape;
  mismatchedDims: string[];
};

type ComplianceRule = {
  id: string;
  name: string;
  description: string;
  status: 'pass' | 'fail' | 'warning';
  details?: string;
  outlier?: Outlier;
};

// A run()-able mutation handle from useMutationWithToast. Kept structural so
// we don't couple to the wrapper's internal return type.
type RemoveHandle = {
  run: (args: { scheduleItemId: Id<'schedule'> }) => Promise<unknown>;
};

// NMW-418 Phase A: re-encode an outlier to the playlist's majority profile
// (a "variant" rendition). Async — the operator re-assigns the version via the
// timeline's "Cambiar versión" picker once the variant is ready.
type ReencodeHandle = {
  run: (args: {
    assetId: Id<'assets'>;
    targetProfile: {
      width: number;
      height: number;
      frameRate: number;
      audioSampleRate?: number;
    };
  }) => Promise<unknown>;
};

type ComplianceResult = {
  overallStatus?: 'pass' | 'fail' | 'warning';
  rules?: ComplianceRule[];
};

// Exported so the /schedules list badge (NMW-452 ScheduleHealthBadge) maps a
// compliance status to a Pill tone through THIS table rather than a hand-copied
// duplicate — one mapping, zero drift between the list and this drawer.
export const TONE_FOR_STATUS = {
  fail: 'bad' as const,
  warning: 'warn' as const,
  pass: 'good' as const,
};

export function ScheduleValidationDrawer({
  open,
  onClose,
  scheduleId,
}: ScheduleValidationDrawerProps) {
  const result = useQuery(
    api.compliance.checkSchedule,
    scheduleId
      ? { scheduleId: scheduleId as unknown as Id<'schedules'> }
      : 'skip',
  ) as ComplianceResult | undefined;

  // Mirrors SchedulesEditor.tsx: the same existing mutation, wrapped with the
  // toast wrapper so real backend errors surface honestly (no fabricated
  // success). RE-ENCODE is deferred — this only removes the offending item.
  const removeM = useMutationWithToast(
    api.schedule.deleteScheduleItemFromContainer,
    {
      logLabel: 'schedule_deleteScheduleItemFromContainer',
      successMessage: 'Elemento eliminado.',
      errorMessagePrefix: 'No se pudo eliminar el elemento',
    },
  ) as unknown as RemoveHandle;

  // NMW-418 Phase A: dispatch a variant re-transcode of the outlier at the
  // playlist's majority profile. Honest wrapper surfaces backend throws; the
  // re-transcode is async (the operator repins when the variant is ready).
  const reencodeM = useMutationWithToast(
    api.transcodes.requestRetranscode,
    {
      logLabel: 'transcodes_requestRetranscode',
      successMessage:
        'Re-codificación iniciada. Podrás re-asignar la versión cuando esté lista.',
      errorMessagePrefix: 'No se pudo iniciar la re-codificación',
    },
  ) as unknown as ReencodeHandle;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Validación"
      width={440}
      // NMW-482: no footer. The former «Volver a validar» button was decorative
      // (no onClick). `checkSchedule` is a Convex query, so this drawer holds a
      // live subscription: every input the rule engine reads (schedule rows,
      // assets, the pinned assetTranscodes rendition) is in the reactive read
      // set, and the panel re-renders itself when any of them change — including
      // when a re-encode dispatched from here lands. A manual re-validate would
      // recompute a pure function of data the client already has and return an
      // identical result, i.e. a fabricated affordance. SlideOver renders the
      // footer bar only when `footer` is truthy, so omitting it leaves no
      // orphaned action strip; the drawer closes via ✕ or the backdrop.
    >
      {!scheduleId ? (
        <EmptyState
          icon="alertTriangle"
          title="Sin programación"
          body="No se localizó la programación a validar."
        />
      ) : result === undefined ? (
        <Skeleton h={320} />
      ) : (
        <ValidationBody
          result={result}
          removeM={removeM}
          reencodeM={reencodeM}
        />
      )}
    </SlideOver>
  );
}

function ValidationBody({
  result,
  removeM,
  reencodeM,
}: {
  result: ComplianceResult;
  removeM: RemoveHandle;
  reencodeM: ReencodeHandle;
}) {
  const rules = result.rules ?? [];
  if (rules.length === 0) {
    return (
      <EmptyState
        icon="shield"
        title="Sin observaciones"
        body="La programación cumple las reglas verificadas."
      />
    );
  }

  // Group rules by status — Errores / Advertencias / Buenas prácticas.
  const groups: Array<['Errores' | 'Advertencias' | 'Buenas prácticas', ComplianceRule[]]> = [
    ['Errores', rules.filter((r) => r.status === 'fail')],
    ['Advertencias', rules.filter((r) => r.status === 'warning')],
    ['Buenas prácticas', rules.filter((r) => r.status === 'pass')],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {groups.map(([title, items]) => {
        const tone =
          title === 'Errores'
            ? TONE_FOR_STATUS.fail
            : title === 'Advertencias'
              ? TONE_FOR_STATUS.warning
              : TONE_FOR_STATUS.pass;
        const toneFg = (TONES as Record<string, { fg?: string }>)[tone]?.fg;
        return (
          <div key={title}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 10,
              }}
            >
              <Pill tone={tone} size="sm">
                {title}
              </Pill>
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--muted-slate)',
                }}
              >
                {items.length}
              </span>
            </div>
            {items.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--lightest-gray)',
                }}
              >
                <Icon
                  name={
                    tone === 'bad'
                      ? 'alertCircle'
                      : tone === 'warn'
                        ? 'alertTriangle'
                        : 'info'
                  }
                  size={16}
                  color={toneFg}
                  style={{ marginTop: 1 }}
                />
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 13, lineHeight: 1.4 }}>
                    {r.details ?? r.description ?? r.name}
                  </span>
                  {r.outlier ? (
                    <div
                      style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                    >
                      <Btn
                        variant="ghost"
                        tone="bad"
                        size="sm"
                        icon="trash"
                        reviewed="wired"
                        onClick={() =>
                          removeM.run({
                            scheduleItemId: r.outlier!
                              .scheduleItemId as unknown as Id<'schedule'>,
                          })
                        }
                      >
                        Quitar de la programación
                      </Btn>
                      {/* Re-encode to the majority profile — only when the
                          majority carries a complete target (requestRetranscode
                          needs width+height+frameRate). */}
                      {r.outlier.majority.width != null &&
                      r.outlier.majority.height != null &&
                      r.outlier.majority.frameRate != null ? (
                        <Btn
                          variant="ghost"
                          tone="warn"
                          size="sm"
                          icon="refresh"
                          reviewed="wired"
                          onClick={() =>
                            reencodeM.run({
                              assetId: r.outlier!
                                .assetId as unknown as Id<'assets'>,
                              targetProfile: {
                                width: r.outlier!.majority.width!,
                                height: r.outlier!.majority.height!,
                                frameRate: r.outlier!.majority.frameRate!,
                                audioSampleRate:
                                  r.outlier!.majority.audioSampleRate,
                              },
                            })
                          }
                        >
                          Re-codificar a {r.outlier.majority.width}×
                          {r.outlier.majority.height}@
                          {r.outlier.majority.frameRate}
                        </Btn>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default ScheduleValidationDrawer;
